import {
  Alert,
  AUTH_JWT_TOKEN,
  AUTH_JWT_TOKEN_EXPIRATION,
  AUTH_REFRESH_TOKEN,
  AUTH_REFRESH_TOKEN_EXPIRATION,
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  Form,
  Grid,
  Link,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { Input, SubmitButton } from "components";
import { AuthRequest, AuthResult, AuthResultType, AuthViewResource, ModelPropertyBase } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppAuthStatus } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { forgotCredentialPageRoute } from "../ForgotCredentialPage/ForgotCredentialPage";
import { EnvInfo } from "./EnvInfo";

const routeName = "login";

class OptionalParams {
  redirectTo?: string = "";
}

export const loginPageRoute: PageRoute<undefined, OptionalParams> = {
  handle: "Login",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
  getOptionalParams: (params: OptionalParams) => buildOptionalParams(params),
};

export const LoginPage = () => {
  const { optionalParams } = useParams(routeName, undefined, OptionalParams);

  const theme = useTheme();

  const [loginModel, setLoginModel] = useState<AuthRequest>({} as AuthRequest);
  const [viewResource, setViewResource] = useState<AuthViewResource>({});
  const properties = viewResource.properties || [];
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    fetchViewResource();
  }, []);

  const authenticate = () => {
    setValidationErrors({});

    ApiService.auth
      .authenticate(loginModel)
      .then((response) => {
        const authResult: AuthResult = response;

        if (authResult.authResultType === AuthResultType.Authenticated) {
          localStorage.setItem(AUTH_JWT_TOKEN, authResult.jwtToken!);
          localStorage.setItem(AUTH_JWT_TOKEN_EXPIRATION, authResult.jwtTokenExpiration?.toString()!);
          localStorage.setItem(AUTH_REFRESH_TOKEN, authResult.refreshToken!);
          localStorage.setItem(AUTH_REFRESH_TOKEN_EXPIRATION, authResult.refreshTokenExpiration?.toString()!);

          dispatch(setAppAuthStatus("AUTHENTICATED"));

          if (!optionalParams.redirectTo) {
            navigate("/");
          } else {
            navigate(optionalParams.redirectTo);
          }
        } else if (authResult.authResultType === AuthResultType.PasswordExpired) {
          setLoginModel(
            produce((draft) => {
              draft.isChangingPassword = true;
            })
          );
        }
      })
      .catch((ex) => {
        setValidationErrors(ex.response);
      });
  };

  const fetchViewResource = () => {
    ApiService.auth.getAuthViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const tenantNameField = () => {
    const propertyName = pn<AuthRequest>("tenantName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={loginModel?.tenantName}
        onChange={(e) =>
          setLoginModel(
            produce((draft) => {
              draft.tenantName = e.target.value;
            })
          )
        }
        name="tenantName"
        hideCount
        disabled={loginModel.isChangingPassword}
      />
    );
  };

  const usernameField = () => {
    const propertyName = pn<AuthRequest>("username");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={loginModel?.username}
        onChange={(e) =>
          setLoginModel(
            produce((draft) => {
              draft.username = e.target.value;
            })
          )
        }
        name="username"
        hideCount
        disabled={loginModel.isChangingPassword}
      />
    );
  };

  const passwordField = () => {
    const propertyName = pn<AuthRequest>("password");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={loginModel?.password}
        onChange={(e) =>
          setLoginModel(
            produce((draft) => {
              draft.password = e.target.value;
            })
          )
        }
        type="password"
        name="password"
        hideCount
        disabled={loginModel.isChangingPassword}
      />
    );
  };

  const newPasswordField = () => {
    const propertyName = pn<AuthRequest>("newPassword");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={loginModel?.newPassword}
        onChange={(e) =>
          setLoginModel(
            produce((draft) => {
              draft.newPassword = e.target.value;
            })
          )
        }
        type="password"
        hideCount
      />
    );
  };

  const confirmNewPasswordField = () => {
    const propertyName = pn<AuthRequest>("confirmNewPassword");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={loginModel?.confirmNewPassword}
        onChange={(e) =>
          setLoginModel(
            produce((draft) => {
              draft.confirmNewPassword = e.target.value;
            })
          )
        }
        type="password"
        hideCount
      />
    );
  };

  return (
    <Page>
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Card sx={{ maxWidth: "500px", width: "100%" }}>
          <Stack px={3} py={7} spacing="lg">
            <Stack spacing="sm" alignItems="center">
              <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
                Sign In
              </Typography>

              <Typography variant="subtitle2">Fill in the fields below to sign into your account.</Typography>
            </Stack>

            <Form>
              <Stack spacing="md">
                {validationErrors?.[pn<AuthRequest>("generalValidationErrors")] && (
                  <Alert severity="error">{validationErrors?.[pn<AuthRequest>("generalValidationErrors")]?.[0]}</Alert>
                )}

                {loginModel.isChangingPassword && (
                  <Alert severity="warning">
                    Your password has expired. Please enter new passwords for your account.
                  </Alert>
                )}

                {tenantNameField()}

                {usernameField()}

                {passwordField()}

                {loginModel.isChangingPassword && (
                  <>
                    {newPasswordField()}
                    {confirmNewPasswordField()}
                  </>
                )}

                <SubmitButton onClick={authenticate} label="Login" fullWidth sx={{ height: "50px" }} />

                <Stack direction="row">
                  <Link
                    ml="auto"
                    underline="hover"
                    onClick={() =>
                      navigate({
                        pathname: `/${forgotCredentialPageRoute.getPath()}`,
                        search: forgotCredentialPageRoute.getOptionalParams?.({
                          tenantName: loginModel.tenantName,
                        }),
                      })
                    }
                  > 
                    Forgot Password/Username
                  </Link>
                </Stack>
              </Stack>
            </Form>
          </Stack>
        </Card>
      </Stack>

      <Box sx={{ position: "absolute", right: 0, bottom: 0 }}>
        <EnvInfo />
      </Box>
    </Page>
  );
};
